import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  Inject,
  PLATFORM_ID,
  ElementRef
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { isPlatformBrowser } from '@angular/common';
import { map, tap } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';

import { MonoFieldValueAccessorComponent } from '../mono-field-value-accessor.component';
import { BrandsEnum } from '../../../_enums/brands.enum';
import { ThemesEnum } from '../../../_enums/themes.enum';
import { SdFormAutocompleteComponent } from '../form-autocomplete/form-autocomplete.component';
import { SdFormLibelleComponent } from '../form-libelle/form-libelle.component';
import { ErrorTranslateService } from '../error-translate-service';
import { BaseFormErrorTranslateService } from '../form-common-error/base-form-error-translate.service';
import { DropdownType } from '../../dropdowns/dropdown/dropdown.enum';
import { SdDropdownComponent } from '../../dropdowns/dropdown/dropdown.component';
import { SdListComponent } from '../../lists/list/list.component';

/**
 * Component of select
 * The component is used in the following way:
 * <sd-form select
 *  [nameField]="'country_1'"
 *  [labelTitle]="'Liste de pays'"
 *  [items]="countryList"
 *  [required]="true"
 *  formControlName="select"
 * </sd-form>
 */
@Component({
  selector: 'sd-form[select]',
  templateUrl: './form-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SdFormSelectComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SdFormSelectComponent),
      multi: true
    },
    { provide: BaseFormErrorTranslateService, useClass: ErrorTranslateService }
  ],
  standalone: true,
  imports: [
    SdFormLibelleComponent,
    FormsModule,
    ReactiveFormsModule,
    SdDropdownComponent,
    SdFormAutocompleteComponent,
    SdListComponent
  ]
})
export class SdFormSelectComponent<T>
  extends MonoFieldValueAccessorComponent<T, string>
  implements ControlValueAccessor, Validator, OnInit, OnChanges, AfterViewInit
{
  constructor(
    cdRef: ChangeDetectorRef,
    protected elRef: ElementRef,
    @Inject(PLATFORM_ID) protected platformId: any,
  ) {
    super(cdRef);
  }

  _libelle: string;
  dropdownType = DropdownType.select;
  buttonSize: number;
  isMobile$: boolean;
  mobileSubscription: Subscription;
  _items: any[];

  @Input()
  brand: BrandsEnum;

  @Input()
  isFocusable = true;

  @Input()
  isOpened = false;

  @Input()
  displayWidth: number = null;

  /**
   * List of objects to display in the select
   */
  @Input()
  set items(items: any[]) {
    this._items = items;
    const selectedItem = items?.find((item) => item?.checked);
    if (selectedItem) {
      this.mapSelectedItem(selectedItem);
    } else {
      this.internalField.reset();
    }
  }
  get items() {
    return this._items;
  }

  /**
   * Key representing the unique identifier of the object
   */
  @Input()
  valueKey: string;

  @Input()
  selectedValue: any;

  /**
   * Key of the objects to be used as display for each "option
   * Can be a getter on the object that returns a formatted label
   */
  @Input()
  libelleKey: string;

  @Input()
  required = false;

  @Input()
  set disabled(value: boolean) {
    this.setDisabledState(value);
  }

  /**
   * Defined the theme
   */
  @Input()
  theme: ThemesEnum.light | ThemesEnum.dark;

  @Input()
  hasAutocomplete = false;

  @Input() class = '';
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.isSmall ? 'is-small' : '',
      this.getClasses(),
      this.theme ? 'is-theme-' + this.theme : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  @Output()
  isOpenedChange = new EventEmitter<boolean>();

  @Output()
  valueChanged = new EventEmitter<any>();

  ngOnInit(): void {
    if (this.selectedValue) {
      this.itemSelected(this.selectedValue);
    } else {
      this.itemSelected(0);
    }
    if (isPlatformBrowser(this.platformId) && this.displayWidth) {
      this.elRef.nativeElement.style.setProperty(
        'width',
        this.displayWidth / 10 + 'rem',
      );
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedValue && this.selectedValue) {
      this.itemSelected(this.selectedValue);
    }
  }

  ngAfterViewInit() {
    if (this.selectedValue) {
      setTimeout(() => {
        this.itemSelected(this.selectedValue);
      });
    }
  }

  mobileSelection(event) {
    if (!this.disabled) {
      const item = this.placeholder
        ? this.items[event.selectedIndex - 1]
        : this.items[event.selectedIndex];
      this.itemSelected(item);
      this.valueChanged.emit(item);
    }
  }

  /**
   * Returns the object of the list having the identifier (see valueKey) passed in parameter
   */
  mappingChildToParent(selectedKey: string): T {
    if (selectedKey) {
      return this.items?.find(
        (item) => String(item[this.valueKey] || '') === String(selectedKey),
      );
    } else {
      return undefined;
    }
  }

  /**
   * Returns the ID of the given object in parameter
   */
  mappingParentToChild(item: T): string {
    return item?.[this.valueKey] || '';
  }

  validate(formControl: AbstractControl): ValidationErrors | null {
    return this.required ? Validators.required(formControl) : null;
  }

  /**
   * Subscribes to changes in the internal form to update the parent value
   */
  updateParent(control: AbstractControl): Observable<T> {
    return control.valueChanges.pipe(
      map((value) => this.mappingChildToParent(value)),
      tap((value) => this.propagateChange(value)),
      tap(() => this.propagateTouched()),
    );
  }

  mapSelectedItem(item) {
    const mappingParentToChildValue = this.mappingParentToChild(item);
    if (mappingParentToChildValue) {
      this.internalField.setValue(mappingParentToChildValue);
    } else {
      this.internalField.setValue(item);
    }
    this.propagateChange(item);
    this.internalField.markAsTouched();
  }

  private itemSelected(item, event?: KeyboardEvent) {
    if (event?.key === 'Enter') {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();
    }

    this.items?.forEach((i) => {
      if (i?.checked != null) {
        i.checked = false;
      }
    });
    if (item?.checked != null) {
      item.checked = true;
    }

    this.mapSelectedItem(item);
    this._libelle = this.internalField?.value
      ? this.mappingChildToParent(this.internalField?.value)?.[this.libelleKey]
      : this.placeholder;
    this.cdRef.markForCheck();
    this.internalField.markAsTouched();
    this.close();
  }

  itemClickSelected(item, event?: Event) {
    this.itemSelected(item, event as KeyboardEvent);
    this.valueChanged.emit(item);
  }

  onChange(item) {
    this.itemSelected(item);
    this.valueChanged.emit(item);
    if (!this.hasAutocomplete) {
      this.close();
    }
  }

  close() {
    this.isOpenedChange.emit(false);
    this.isOpened = false;
  }
}
